import { api } from './api';

export type CommunicationChannel = 'EMAIL' | 'WHATSAPP';
export interface CommunicationAudience {
  type: 'ALL' | 'GROUPS';
  groupIds: string[];
}
export interface CommunicationMessage {
  automationId?: string;
  templateName?: string;
  language?: string;
  bindings?: Record<string, string>;
}
export interface CommunicationDraft {
  name: string;
  channel: CommunicationChannel;
  audience: CommunicationAudience;
  message: CommunicationMessage;
}
export type CommunicationTriggerType =
  | 'COURSE_ACCESS_AGE'
  | 'INACTIVE_DAYS'
  | 'MODULE_COMPLETED'
  | 'LESSON_COMPLETED'
  | 'USER_REGISTERED';
export interface CommunicationTrigger {
  type: CommunicationTriggerType;
  days?: number;
  memberAreaId?: string;
  productId?: string;
  lessonId?: string;
}
export interface AutomationDraft extends CommunicationDraft {
  trigger: CommunicationTrigger;
}
export interface CommunicationAutomation extends AutomationDraft {
  id: string;
  revision: number;
  isActive: boolean;
  enabledAt: string | null;
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED';
  createdAt: string;
  updatedAt: string;
}
export interface CommunicationCampaign extends CommunicationDraft {
  id: string;
  revision: number;
  status: 'DRAFT' | 'QUEUED' | 'COMPLETED' | 'CANCELLED';
  recipientCount: number;
  queuedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface CommunicationCatalog {
  channels: CommunicationChannel[];
  triggers: {
    type: CommunicationTriggerType;
    label: string;
    requiresDays: boolean;
    requiresMemberArea: boolean;
    requiresProduct: boolean;
    requiresLesson: boolean;
  }[];
  variables: { key: string; label: string }[];
  memberAreas: { id: string; name: string }[];
  products: { id: string; title: string; memberAreaId: string | null }[];
  lessons: { id: string; title: string; productIds: string[] }[];
}
export interface CommunicationPage<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}
export interface CommunicationPreview {
  eligibleRecipients: number;
  excludedRecipients: number;
  totalRecipients: number;
  exclusions: Record<string, number>;
  previewToken: string;
  expiresAt: string;
  revision: number;
  readiness: { ready: boolean; reason?: string };
}
export interface CommunicationDelivery {
  id: string;
  channel: CommunicationChannel;
  status: string;
  attempts: number;
  reason: string | null;
  automationId: string | null;
  campaignId: string | null;
  createdAt: string;
  acceptedAt: string | null;
  deliveredAt: string | null;
}
export interface CommunicationPreferencesValue {
  email: boolean;
  whatsapp: boolean;
}
export type CommunicationProvider = 'activecampaign' | 'meta';
export interface ProviderStatus {
  provider: CommunicationProvider;
  configured: boolean;
  configurationAvailable?: boolean;
  enabled: boolean;
  configurationRevision: string;
  settings: Record<string, string>;
  hasToken?: boolean;
  hasAppSecret?: boolean;
  hasVerifyToken?: boolean;
  reason?: string;
}
export interface ActiveCampaignConfig {
  enabled: boolean;
  accountUrl: string;
  listId: string;
  apiToken?: string;
}
export interface MetaConfig {
  enabled: boolean;
  wabaId: string;
  phoneNumberId: string;
  graphVersion: string;
  accessToken?: string;
  appSecret?: string;
  verifyToken?: string;
}
export interface ActiveCampaignAutomation {
  id: string;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
}
export interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  status: string;
  body: string;
  parameters: { key: string; label: string; kind: string; example?: string }[];
  unsupportedReason?: string;
}
export interface WhatsAppTemplateDraft {
  name: string;
  language: string;
  category: 'MARKETING' | 'UTILITY';
  body: string;
  bodyExamples?: Record<string, string>;
  header?: { text: string; examples?: Record<string, string> };
  footer?: string;
  buttons?: { text: string; url: string; example?: string }[];
}

function unwrap<T>(value: T | { data: T }): T {
  return value &&
    typeof value === 'object' &&
    'data' in value &&
    !('meta' in value)
    ? value.data
    : (value as T);
}
const prefix = '/communications';
const writeOptions = { retryOnNetworkError: false };
async function get<T>(path: string): Promise<T> {
  return unwrap((await api.get<T | { data: T }>(`${prefix}${path}`)).data);
}
async function post<T>(path: string, body: unknown = {}): Promise<T> {
  return unwrap(
    (await api.post<T | { data: T }>(`${prefix}${path}`, body, writeOptions))
      .data,
  );
}
async function put<T>(path: string, body: unknown): Promise<T> {
  return unwrap(
    (await api.put<T | { data: T }>(`${prefix}${path}`, body, writeOptions))
      .data,
  );
}

export const communicationsService = {
  catalog: () => get<CommunicationCatalog>('/admin/catalog'),
  automations: (page = 1) =>
    get<CommunicationPage<CommunicationAutomation>>(
      `/admin/automations?page=${page}&limit=20`,
    ),
  campaigns: (page = 1) =>
    get<CommunicationPage<CommunicationCampaign>>(
      `/admin/campaigns?page=${page}&limit=20`,
    ),
  createAutomation: (draft: AutomationDraft) =>
    post<CommunicationAutomation>('/admin/automations', draft),
  updateAutomation: (id: string, draft: AutomationDraft, revision: number) =>
    put<CommunicationAutomation>(`/admin/automations/${id}`, {
      ...draft,
      revision,
    }),
  activateAutomation: (id: string, isActive: boolean, revision: number) =>
    post<CommunicationAutomation>(`/admin/automations/${id}/activation`, {
      isActive,
      revision,
    }),
  createCampaign: (draft: CommunicationDraft) =>
    post<CommunicationCampaign>('/admin/campaigns', draft),
  updateCampaign: (id: string, draft: CommunicationDraft, revision: number) =>
    put<CommunicationCampaign>(`/admin/campaigns/${id}`, {
      ...draft,
      revision,
    }),
  previewCampaign: (id: string, revision: number) =>
    post<CommunicationPreview>(`/admin/campaigns/${id}/preview`, { revision }),
  sendCampaign: (
    id: string,
    previewToken: string,
    expectedRecipients: number,
  ) =>
    post<CommunicationCampaign>(`/admin/campaigns/${id}/send`, {
      previewToken,
      expectedRecipients,
    }),
  cancelCampaign: (id: string, revision: number) =>
    post<CommunicationCampaign>(`/admin/campaigns/${id}/cancel`, { revision }),
  deliveries: (
    page = 1,
    status = '',
    filters: { campaignId?: string; automationId?: string } = {},
  ) =>
    get<
      CommunicationPage<CommunicationDelivery> & {
        counts: Record<string, number>;
      }
    >(
      `/admin/deliveries?page=${page}&limit=20${status ? `&status=${encodeURIComponent(status)}` : ''}${filters.campaignId ? `&campaignId=${encodeURIComponent(filters.campaignId)}` : ''}${filters.automationId ? `&automationId=${encodeURIComponent(filters.automationId)}` : ''}`,
    ),
  providers: () => get<ProviderStatus[]>('/admin/providers'),
  saveActiveCampaign: (config: ActiveCampaignConfig) =>
    put<ProviderStatus>('/admin/providers/activecampaign', config),
  saveMeta: (config: MetaConfig) =>
    put<ProviderStatus>('/admin/providers/meta', config),
  disableProvider: (provider: CommunicationProvider) =>
    put<ProviderStatus>(`/admin/providers/${provider}`, { enabled: false }),
  testProvider: (provider: CommunicationProvider) =>
    post<{ connected: boolean; message?: string }>(
      `/admin/providers/${provider}/test`,
    ),
  emailAutomations: () =>
    get<{ items: ActiveCampaignAutomation[] }>(
      '/admin/providers/activecampaign/automations',
    ),
  whatsappTemplates: () =>
    get<{ items: WhatsAppTemplate[] }>('/admin/providers/meta/templates'),
  syncWhatsAppTemplates: () =>
    post<{ items: WhatsAppTemplate[] }>('/admin/providers/meta/templates/sync'),
  createWhatsAppTemplate: (draft: WhatsAppTemplateDraft) =>
    post<{ id: string; status: string; category: string }>(
      '/admin/providers/meta/templates',
      draft,
    ),
  preferences: () => get<CommunicationPreferencesValue>('/preferences'),
  savePreferences: (value: CommunicationPreferencesValue) =>
    put<CommunicationPreferencesValue>('/preferences', value),
  activity: () => post<{ recorded: boolean }>('/activity'),
};
